import { TriggerProps } from '@atlaskit/popup';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { colors } from '@atlaskit/theme';
import { HoverProps, useHoverState } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import {
  selectCurrentSelectionMode,
  selectSelectedAdjacentCells,
  selectIsValueSelected,
  selectIsSoftSelectEnabled,
  selectIsHoverDeleteEntriesButton,
  selectIsHoverDeleteFieldsButton,
  selectSelectedFieldIds,
  selectSelectedEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseViewSettings } from '@databases/types/database/view';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, { MutableRefObject, Ref, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { TABLE_CLASSNAMES } from '../../../../constants';
import { DragDirection, DragType } from '../../../drag-utils';
import { getHeaderCellAtIndex } from '../../../table';
import { useScope } from '../../Scope';
import DatabaseValueInlineEdit from '../../database-value/DatabaseValueInlineEdit';
import { EditMode, FIELD_TYPES } from '../../field-types/fieldTypes';
import { selectedBorderWidth } from '../../styled';
import { Position } from '../contextual-menu/type';

import AddEntryDot from './AddEntryDot';
import AddFieldDot from './AddFieldDot';
import DragCaret from './DragCaret';

const DragToFillIndicator = styled.div<{ position: Position }>`
  :after {
    content: ' ';
    position: absolute;
    ${({ position }) => (position === Position.BEFORE ? 'top: -3px;' : 'bottom: -3px;')}
    left: -${selectedBorderWidth / 2}px;
    width: calc(100% + ${selectedBorderWidth}px);
    height: 6px;
    background-color: ${colors.B200};
  }
`;

export type TableLayoutCellProps = {
  contentId: string;
  entryId: string;
  fieldId: string;
  viewSettings?: DatabaseViewSettings;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds?: MutableRefObject<Set<string>>;
  isFirstInRow: boolean;
  isLastInRow: boolean;
  isLastInColumn: boolean;
  isSelectionDisabled?: boolean;
  isInsertContentDisabled?: boolean;
  showAsDisabled?: boolean;
  addRowBeforeButtonHoverProps: HoverProps;
  addRowAfterButtonHoverProps: HoverProps;
  currentViewId: string;
  onIsSelectedChange?: (options?: {
    resetSelection?: boolean;
    isMultiSelectOn?: boolean;
  }) => void;
  handleOnContextMenu?: (event: React.MouseEvent) => void;
};

const TableLayoutCell = React.memo(
  React.forwardRef<HTMLTableSectionElement, TableLayoutCellProps & Partial<TriggerProps>>(
    function TableLayoutCell(
      {
        contentId,
        entryId,
        fieldId,
        viewSettings,
        isContentEditable,
        canEditContent,
        canEditFields,
        editingIds,
        isFirstInRow,
        isLastInRow,
        isLastInColumn,
        isSelectionDisabled,
        isInsertContentDisabled,
        showAsDisabled,
        addRowBeforeButtonHoverProps,
        addRowAfterButtonHoverProps,
        currentViewId,
        onIsSelectedChange,
        handleOnContextMenu,
        ...rest
      },
      ref
    ) {
      const scope = useScope();
      const [
        isFirstField,
        markAsDeleted,
        isSelected,
        isSoftSelected,
        selectedAdjacentCells,
        showInsertColumnBefore,
        showInsertColumnAfter,
        showInsertRowBefore,
        showInsertRowAfter,
        canEditValue,
        isLastSelectedRow,
        isFirstSelectedRow,
        isLastSelectedCell,
      ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const databaseDraft = selectDatabaseDraftByContentId(state, {
          contentId,
          scope,
          viewSettings,
          includeDeleted: true,
        });

        const isSoftSelected = !isSelectionDisabled && selectIsSoftSelectEnabled(state);

        const isSelected =
          !isSelectionDisabled && selectIsValueSelected(state, { entryId, fieldId });

        const selectedFieldIds = isSelected
          ? selectSelectedFieldIds(state, {
              contentId,
              scope,
              viewSettings,
            })
          : [];

        const selectedEntryIds = isSelected
          ? selectSelectedEntryIds(state, {
              contentId,
              scope,
              viewSettings,
            })
          : [];

        const selectedAdjacentCells = isSelected
          ? selectSelectedAdjacentCells(state, {
              entryId,
              fieldId,
              scope,
              contentId,
              viewSettings,
            })
          : undefined;
        const currentSelectionMode = selectCurrentSelectionMode(state, {
          contentId,
          scope,
          viewSettings,
        });
        const isCellNotSelected = !(isSelected && currentSelectionMode === 'cell');
        const field = getDatabaseFieldRequired(databaseDraft, fieldId);
        const entry = getDatabaseEntryRequired(databaseDraft, entryId);
        const value = getDatabaseValue(databaseDraft, field, entry);
        const canEditValue =
          FIELD_TYPES[field.type].editMode(
            {
              database: databaseDraft,
              field,
              entry,
              value,
            },
            state
          ) !== EditMode.READ_ONLY;

        const isHoverDeleteEntriesButton =
          isSelected && selectIsHoverDeleteEntriesButton(state);
        const isHoverDeleteFieldsButton =
          isSelected && selectIsHoverDeleteFieldsButton(state);

        const isLastSelectedColumn =
          selectedFieldIds[selectedFieldIds.length - 1] === fieldId;
        const isLastSelectedRow =
          selectedEntryIds[selectedEntryIds.length - 1] === entryId;
        const isFirstSelectedRow = selectedEntryIds[0] === entryId;

        const isLastSelectedCell =
          isSelected &&
          ((currentSelectionMode === 'cell' &&
            isLastSelectedRow &&
            isLastSelectedColumn) ||
            (currentSelectionMode === 'row' && isLastInRow && isLastSelectedRow) ||
            (currentSelectionMode === 'column' &&
              isLastInColumn &&
              isLastSelectedColumn));

        return [
          fieldId === databaseDraft.fields[0],
          isHoverDeleteEntriesButton || isHoverDeleteFieldsButton,
          isSelected,
          isSoftSelected,
          selectedAdjacentCells,
          isCellNotSelected && !selectedAdjacentCells?.left && !isInsertContentDisabled,
          isCellNotSelected && !selectedAdjacentCells?.right && !isInsertContentDisabled,
          isCellNotSelected && !selectedAdjacentCells?.top && !isInsertContentDisabled,
          isCellNotSelected && !selectedAdjacentCells?.bottom && !isInsertContentDisabled,
          canEditValue,
          isLastSelectedRow,
          isFirstSelectedRow,
          isLastSelectedCell,
        ] as const;
      });

      const initialIsEditing = isFirstField && editingIds?.current.has(entryId);

      if (initialIsEditing) {
        // Since we use editingIds only to set the initialIsEditing prop, it is enough to set it once during the initial
        // render of the inline edit field. We don't want the field to steal the focus again when it is hidden and then
        // shown again because of a filter change. Because of this, we remove the entry from the list once it has been
        // focused for the first time.
        editingIds!.current.delete(entryId);
      }
      const cellRef = useRef<HTMLTableCellElement | null>(null);

      const [isHoverInsertFieldDot, setIsHoverInsertFieldDot] = useState(false);
      const handleMouseEnterInsertField = (position: Position) => {
        setIsHoverInsertFieldDot(true);
        const headerCellAtIndex = getHeaderCellAtIndex(cellRef?.current);
        headerCellAtIndex?.classList.add(
          position === Position.BEFORE
            ? TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE
            : TABLE_CLASSNAMES.INSERT_COLUMN_AFTER
        );
      };

      const handleMouseLeaveInsertField = (position: Position) => {
        setIsHoverInsertFieldDot(false);
        const headerCellAtIndex = getHeaderCellAtIndex(cellRef?.current);
        headerCellAtIndex?.classList.remove(
          position === Position.BEFORE
            ? TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE
            : TABLE_CLASSNAMES.INSERT_COLUMN_AFTER
        );
      };

      const [dragDirectionWhenSelected, setDragDirectionWhenSelected] =
        useState<DragDirection | null>(null);
      useEffect(
        () =>
          monitorForElements({
            canMonitor: ({ source }) => source.data.type === DragType.FILL_VALUES,
            onDrag: ({ location }) => {
              const dragDirection =
                location.initial.input.clientY <= location.current.input.clientY
                  ? DragDirection.DOWN
                  : DragDirection.UP;
              if (isSelected) {
                setDragDirectionWhenSelected(dragDirection);
              }
            },
            onDrop: () => setDragDirectionWhenSelected(null),
          }),
        [contentId, isSelected, scope]
      );

      const [isHoverCell, hoverProps] = useHoverState();
      const dispatch = defaultStoreUtils.useDispatch();

      return (
        <td
          {...rest}
          {...hoverProps}
          onContextMenu={handleOnContextMenu}
          ref={composeRefs(cellRef, ref as Ref<HTMLTableCellElement>)}
          onBlur={() => {
            dispatch(deselectAll());
          }}
          className={classNames([
            {
              [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
              [TABLE_CLASSNAMES.IS_SOFT_SELECTED]: isSoftSelected,
              [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled,
            },
          ])}
        >
          {!isFirstInRow && showInsertColumnBefore && (
            <AddFieldDot
              contentId={contentId}
              fieldId={fieldId}
              position={Position.BEFORE}
              canEditFields={canEditFields}
              hoverProps={{
                onMouseEnter: () => handleMouseEnterInsertField(Position.BEFORE),
                onMouseLeave: () => handleMouseLeaveInsertField(Position.BEFORE),
              }}
              currentViewId={currentViewId}
            />
          )}
          {showInsertRowBefore && (
            <AddEntryDot
              contentId={contentId}
              entryId={entryId}
              position={Position.BEFORE}
              canEditContent={canEditContent}
              viewSettings={viewSettings}
              hoverProps={addRowBeforeButtonHoverProps}
            />
          )}
          <DatabaseValueInlineEdit
            contentId={contentId}
            fieldId={fieldId}
            entryId={entryId}
            initialIsEditing={initialIsEditing}
            isReadonly={!canEditContent}
            isContentEditable={isContentEditable}
            isSelected={isSelected && !showAsDisabled}
            onIsSelectedChange={onIsSelectedChange}
            isFirstInRow={isFirstInRow}
            selectedAdjacentCells={selectedAdjacentCells}
            enableSelection
            markAsDeleted={markAsDeleted}
          />
          {showInsertRowAfter && (
            <AddEntryDot
              contentId={contentId}
              entryId={entryId}
              position={Position.AFTER}
              canEditContent={canEditContent}
              viewSettings={viewSettings}
              hoverProps={addRowAfterButtonHoverProps}
            />
          )}
          {showInsertColumnAfter && (
            <AddFieldDot
              contentId={contentId}
              fieldId={fieldId}
              position={Position.AFTER}
              canEditFields={canEditFields}
              hoverProps={{
                onMouseEnter: () => handleMouseEnterInsertField(Position.AFTER),
                onMouseLeave: () => handleMouseLeaveInsertField(Position.AFTER),
              }}
              currentViewId={currentViewId}
            />
          )}
          {((!dragDirectionWhenSelected && isLastSelectedCell) ||
            (isHoverCell && !isSelected)) &&
            canEditValue &&
            !isHoverInsertFieldDot && (
              <DragCaret
                contentId={contentId}
                scope={scope}
                viewSettings={viewSettings}
                canEditContent={canEditContent}
                isSelected={isSelected}
                fieldId={fieldId}
                entryId={entryId}
                isDeletionActive={markAsDeleted}
                onIsSelectedChange={onIsSelectedChange}
              />
            )}
          {isSelected &&
            dragDirectionWhenSelected === DragDirection.UP &&
            isFirstSelectedRow && <DragToFillIndicator position={Position.BEFORE} />}
          {isSelected &&
            dragDirectionWhenSelected === DragDirection.DOWN &&
            isLastSelectedRow && <DragToFillIndicator position={Position.AFTER} />}
        </td>
      );
    }
  )
);

export default TableLayoutCell;
